import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ImageSlider from '../../../../components-legacy/ImageSlider';
import {
  GalleryFooter,
  GalleryFooterBrands,
  GalleryFooterControls,
} from '../../../../styled/PageGrid/product';
import Distance from '../../../../styled/Distance';
import ImageSliderControls from './ImageSliderControls';
import Labels from './Labels';
import Label from './Label';
import { formatImage, mapImageSources } from '../../../../utils/image';
import useContext from '../../../../hooks/useContext';
import FullscreenGallery from '../../../../components-legacy/FullscreenGallery';
import { ProductContext } from '../providers/ProductProvider';
import { ImagesContext } from '../providers/ImagesProvider';
import { Column, Columns } from '../../../../styled/Columns/wrappers';
import CertificationInformation from '../ProductInfo/CertificationInformation';

interface IResizedImage extends Omit<IResponsivePicture, 'image' | 'src' | 'srcSet' | 'sizes'> {
  className: string | undefined;
  fallbackImage: IResponsiveImage;
}

const imageSizes = [
  { breakpoint: 360, width: 312 },
  { breakpoint: 440, width: 392 },
  { breakpoint: 640, width: 592 },
  { breakpoint: 899, width: 851 },
  { breakpoint: 1200, width: 640 },
  { breakpoint: 1920, width: 812 },
];

const ProductMedia = () => {
  const [index, setIndex] = useState(0);

  const { generalCertifications, productCertifications, brand, variantId } =
    useContext(ProductContext);

  const {
    closeFullscreenGallery,
    environmentImages,
    measurementsImages,
    productImages,
    fullscreenIndex,
    setFullscreenIndex,
    videos,
  } = useContext(ImagesContext);

  const fullscreenThumbnails = useMemo(() => {
    return [
      ...productImages.map(({ url }) => ({
        src: formatImage(url, {
          width: 56,
          height: 56,
          cdn: true,
          output: 'jpg',
        }),
        type: 'image',
      })),

      ...(measurementsImages || []).map(({ url }) => ({
        src: formatImage(url, {
          width: 56,
          height: 56,
          cdn: true,
          output: 'jpg',
        }),
        type: 'image',
      })),

      ...(environmentImages || []).map(({ url }) => ({
        src: formatImage(url, {
          width: 56,
          height: 56,
          cdn: true,
          output: 'jpg',
        }),
        type: 'image',
      })),

      ...(videos || []).map((youtubeId) => ({
        src: `https://img.youtube.com/vi/${youtubeId}/3.jpg`,
        type: 'video',
      })),
    ];
  }, [environmentImages, productImages, measurementsImages, videos]);

  const fullscreenImages = useMemo(
    () =>
      [...productImages, ...measurementsImages, ...environmentImages].map(({ url }) => ({
        src: formatImage(url, {
          width: 1920,
          height: 1920,
          cdn: true,
          output: 'jpg',
        }),
        sources: [
          {
            srcSet: formatImage(url, {
              width: 1920,
              height: 1920,
              cdn: true,
              output: 'webp',
            }),
            type: 'image/webp',
          },
        ],
      })),
    [environmentImages, productImages, videos, measurementsImages],
  );

  const onImageClick = useCallback(() => {
    setFullscreenIndex(index);
  }, [index]);

  useEffect(() => {
    setIndex(0);
  }, [environmentImages, productImages, measurementsImages]);

  const resizedImages = useMemo(() => {
    const imageList: IResizedImage[] = [];

    [...productImages, ...measurementsImages, ...environmentImages].forEach(
      ({ url, altText }, i) => {
        const imageObject = {
          fallbackImage: {
            ...mapImageSources(
              formatImage(url, { width: 851, height: 597, cdn: true }),
              imageSizes,
              true,
              'jpg',
            ),
            alt: altText,
          },
          sources: [
            {
              type: 'image/webp',
              ...mapImageSources(
                formatImage(url, {
                  width: 851,
                  height: 597,
                  cdn: true,
                  output: 'webp',
                }),
                imageSizes,
                true,
              ),
            },
          ],
          className:
            i >= productImages.length + measurementsImages.length ? 'hidden-md' : undefined,
        };
        imageList.push(imageObject);
      },
    );

    return imageList;
  }, [environmentImages, productImages, measurementsImages, videos]);

  const videoItems = useMemo(() => videos?.map((v) => ({ youtubeId: v })), [videos]);

  const resizedThumbnails = useMemo(() => {
    return [
      ...productImages.map(({ url, altText }) => ({
        src: formatImage(url, {
          width: 94,
          height: 94,
          cdn: true,
          output: 'jpg',
        }),
        sources: [
          {
            type: 'image/webp',
            srcSet: formatImage(url, {
              width: 94,
              height: 94,
              cdn: true,
              output: 'webp',
            }),
          },
        ],
        type: 'image',
        alt: altText,
      })),
      ...(measurementsImages || []).map(({ url }) => ({
        src: formatImage(url, {
          width: 94,
          height: 94,
          cdn: true,
          output: 'webp',
        }),
        type: 'image',
      })),

      ...(videos || []).map((youtubeId) => ({
        sources: [],
        src: `https://img.youtube.com/vi/${youtubeId}/3.jpg`,
        type: 'video',
        alt: undefined,
      })),
    ];
  }, [environmentImages, productImages, measurementsImages, videos]);

  const multipleItems = resizedImages.length + videos.length > 1;
  const multipleProductMedia = productImages.length + measurementsImages.length + videos.length > 1;

  return (
    <>
      <ImageSlider
        id={variantId}
        multiFormatImages={resizedImages}
        showControls={multipleProductMedia}
        index={index}
        onClick={onImageClick}
        onIndexChange={setIndex}
        videos={videoItems}
      />

      <Distance size={['1', '2']} />

      <GalleryFooter>
        <GalleryFooterBrands>
          <Columns className="valign-middle">
            <Labels
              labels={generalCertifications}
              panelTitle="Miljö och kvalitetsmärkningar"
              panelItem={
                generalCertifications?.length ? (
                  <CertificationInformation productCertifications={productCertifications} />
                ) : undefined
              }
            />
            <Column className="grow" />
            <Label name={brand?.name} image={brand?.image} />
          </Columns>
        </GalleryFooterBrands>

        {multipleItems && (
          <GalleryFooterControls>
            <ImageSliderControls
              imageCount={resizedImages.length + videos.length}
              srcs={resizedThumbnails}
              index={index}
              onIndexChange={setIndex}
            />
          </GalleryFooterControls>
        )}
      </GalleryFooter>

      {fullscreenIndex >= 0 && (
        <FullscreenGallery
          images={fullscreenImages}
          thumbnails={fullscreenThumbnails}
          index={fullscreenIndex}
          onClose={closeFullscreenGallery}
          videos={videos}
        />
      )}
    </>
  );
};

export default ProductMedia;
